/**
 * Phones contracts (BUILD-PLAN M16, 5.5 WP3-PHONES; research/library/phones.md 4).
 *
 * The phone screen, the live call pop, click-to-call, the call log, recordings
 * with transcript and AI summary, PBX extension mapping and the own-number
 * guard. Telephony-provider shapes (CallEvent, TelephonyBinding, ...) live in
 * `@velora/telephony-gateway`; these are the Velora API shapes built on top.
 *
 * Money fields are `null` when the caller lacks `money.view` (BUILD-PLAN 2.6),
 * never zero. Phone numbers are E.164 on the wire; the UI formats and masks.
 */
import { z } from 'zod';
import { CallAiSchema } from './phones-ai.js';

const Uuid = z.uuid();
const Iso = z.iso.datetime({ offset: true });
const IsoDate = z.iso.date();
export const E164Schema = z.string().regex(/^\+[1-9][0-9]{6,14}$/, 'expected an E.164 number');

/* ------------------------------------------------------------------------ */
/* Vocabulary                                                                */
/* ------------------------------------------------------------------------ */

export const PhonesProviderSchema = z.enum(['stratus', 'threecx', 'mock']);
export type PhonesProvider = z.infer<typeof PhonesProviderSchema>;

export const PROVIDER_LABEL: Record<PhonesProvider, string> = {
  stratus: 'Stratus',
  threecx: '3CX',
  mock: 'Simulated',
};

/**
 * Who delivered a recorded call: an office PBX, or `recep` for a call the AI
 * receptionist service answered on its own number and reported to Velora.
 */
export const CallSourceProviderSchema = z.enum(['stratus', 'threecx', 'mock', 'recep']);
export type CallSourceProvider = z.infer<typeof CallSourceProviderSchema>;
export const CALL_SOURCE_LABEL: Record<CallSourceProvider, string> = {
  ...PROVIDER_LABEL,
  recep: 'AI receptionist line',
};

/**
 * One plain-English disposition per call (phones.md 4.3), never a raw switch
 * code. `missed_*` are "truly missed": no human or AI answered on any leg.
 */
export const CallDispositionSchema = z.enum([
  'answered',
  'answered_ai',
  'missed_rang_out',
  'missed_ivr',
  'abandoned_queue',
  'voicemail',
  'after_hours',
  'spam',
  'internal',
  'outbound_connected',
  'outbound_no_answer',
]);
export type CallDisposition = z.infer<typeof CallDispositionSchema>;

/** Dispositions that count as a missed inbound call (voicemail is its own lane). */
export const MISSED_DISPOSITIONS: readonly CallDisposition[] = [
  'missed_rang_out',
  'missed_ivr',
  'abandoned_queue',
  'after_hours',
];

export const CallDirectionSchema = z.enum(['inbound', 'outbound', 'internal']);
export type CallDirection = z.infer<typeof CallDirectionSchema>;

/** Wrap-up chips (phones.md 4.2). */
export const WrapupOutcomeSchema = z.enum([
  'booked',
  'not_booked',
  'info',
  'billing',
  'callback',
  'wrong_number',
  'spam',
]);
export type WrapupOutcome = z.infer<typeof WrapupOutcomeSchema>;

export const WRAPUP_LABEL: Record<WrapupOutcome, string> = {
  booked: 'Booked',
  not_booked: 'Not booked',
  info: 'Info only',
  billing: 'Billing',
  callback: 'Call back',
  wrong_number: 'Wrong number',
  spam: 'Spam',
};

export const NotBookedReasonSchema = z.enum([
  'price',
  'insurance',
  'timing',
  'needs_spouse',
  'shopping',
  'other',
]);
export type NotBookedReason = z.infer<typeof NotBookedReasonSchema>;

export const NOT_BOOKED_LABEL: Record<NotBookedReason, string> = {
  price: 'Price',
  insurance: 'Insurance',
  timing: 'Timing',
  needs_spouse: 'Needs to ask spouse',
  shopping: 'Shopping around',
  other: 'Other',
};

/** Who set the outcome: staff in the wrap-up, a deskphone feature code, or the AI. */
export const OutcomeSourceSchema = z.enum(['user', 'keypad', 'ai']);
export type OutcomeSource = z.infer<typeof OutcomeSourceSchema>;

/**
 * Deskphone feature codes (phones.md 4.2, CallRail "Keypad Scoring"): dialled
 * right after hanging up, they set the outcome of that user's last call.
 */
export const KEYPAD_OUTCOME = {
  '*81': 'booked',
  '*82': 'not_booked',
  '*83': 'info',
  '*84': 'spam',
} as const satisfies Record<string, WrapupOutcome>;
export type KeypadCode = keyof typeof KEYPAD_OUTCOME;

/**
 * Signed relay event `POST /v1/phones/webhooks/keypad`: the PBX saw extension
 * `extension` dial a feature code. The office comes from `did` (one of its
 * numbers), exactly like call events; the tenant must own that number.
 */
export const KeypadEventSchema = z.object({
  tenantId: z.string().min(1),
  provider: z.enum(['stratus', 'threecx', 'mock']),
  did: z.string().min(1),
  extension: z.string().regex(/^[0-9]{2,6}$/),
  code: z.string().min(1).max(8),
  at: z.iso.datetime(),
});
export type KeypadEvent = z.infer<typeof KeypadEventSchema>;

/** How the caller ID resolved (own-number guard first, then the household). */
export const CallerKindSchema = z.enum([
  'patient',
  'lead',
  'unknown',
  'withheld',
  'own_number',
  'unmatchable',
]);
export type CallerKind = z.infer<typeof CallerKindSchema>;

/* ------------------------------------------------------------------------ */
/* Call log                                                                  */
/* ------------------------------------------------------------------------ */

export const CallCallerSchema = z.object({
  /** E.164, or null when withheld. */
  number: E164Schema.nullable(),
  kind: CallerKindSchema,
  patientId: Uuid.nullable(),
  /**
   * Patient (preferred first name + last) or lead/contact name; for an
   * `unknown` caller the caller ID name (CNAM, carrier or lookup), shown as
   * caller ID; null when none.
   */
  name: z.string().nullable(),
  /** Active medical or premed alert on the matched patient. */
  alert: z.boolean(),
});
export type CallCaller = z.infer<typeof CallCallerSchema>;

export const CallRowSchema = z.object({
  id: Uuid,
  locationId: Uuid,
  startedAt: Iso,
  direction: CallDirectionSchema,
  caller: CallCallerSchema,
  /** Office line the call came in on (or went out from), as its label. */
  lineLabel: z.string().nullable(),
  /** "Riley Frontdesk", "AI receptionist", "Voicemail" or null (nobody). */
  handledBy: z.string().nullable(),
  ringSeconds: z.number().int().nonnegative().nullable(),
  talkSeconds: z.number().int().nonnegative().nullable(),
  disposition: CallDispositionSchema,
  dispositionLabel: z.string(),
  topic: z.string().nullable(),
  legCount: z.number().int().positive(),
  hasRecording: z.boolean(),
  hasTranscript: z.boolean(),
  outcome: WrapupOutcomeSchema.nullable(),
  outcomeSource: OutcomeSourceSchema.nullable(),
  /** Production booked from this call (null without money.view or when nothing was booked). */
  bookedCents: z.number().int().nonnegative().nullable(),
  booked: z.boolean(),
});
export type CallRow = z.infer<typeof CallRowSchema>;

export const CallFilterSchema = z.enum([
  'all',
  'inbound',
  'outbound',
  'missed',
  'voicemail',
  'recorded',
]);
export type CallFilter = z.infer<typeof CallFilterSchema>;

export const CallListQuerySchema = z.object({
  filter: CallFilterSchema.default('all'),
  q: z.string().trim().max(80).optional(),
  /** Keyset cursor: calls that started strictly before this instant. */
  before: Iso.optional(),
  /** Calls on this practice-local date only (defaults to the last 30 days). */
  date: IsoDate.optional(),
  limit: z.coerce.number().int().min(1).max(200).default(100),
});
export type CallListQuery = z.infer<typeof CallListQuerySchema>;

export const CallListResponseSchema = z.object({
  items: z.array(CallRowSchema),
  nextBefore: Iso.nullable(),
});
export type CallListResponse = z.infer<typeof CallListResponseSchema>;

export const CallLegSchema = z.object({
  leg: z.number().int().positive(),
  kind: z.enum(['desk', 'ring_group', 'queue', 'voicemail', 'ai', 'ivr', 'external']),
  extension: z.string().nullable(),
  /** Mapped Velora user or the extension's label. */
  name: z.string().nullable(),
  ringSeconds: z.number().int().nonnegative().nullable(),
  answered: z.boolean(),
});
export type CallLeg = z.infer<typeof CallLegSchema>;

export const TranscriptSegmentSchema = z.object({
  startMs: z.number().int().nonnegative(),
  /** `unknown`: a mono PBX recording transcribed without speaker separation. */
  speaker: z.enum(['staff', 'caller', 'ai', 'unknown']),
  text: z.string(),
});
export type TranscriptSegment = z.infer<typeof TranscriptSegmentSchema>;

export const CallSummarySchema = z.object({
  text: z.string(),
  /** Follow-up items the summary proposes (never auto-executed). */
  followUps: z.array(z.string()),
  topics: z.array(z.string()),
  callerSentiment: z.enum(['positive', 'neutral', 'negative']).nullable(),
  staffSentiment: z.enum(['positive', 'neutral', 'negative']).nullable(),
  /** `ai` from the gateway; `import` when it came with the transcript. */
  source: z.enum(['ai', 'import']),
  model: z.string().nullable(),
  generatedAt: Iso.nullable(),
});
export type CallSummary = z.infer<typeof CallSummarySchema>;

/**
 * `POST /v1/phones/calls/:id/summary/file`: staff file a reviewed summary.
 * The summary goes on the patient's contact log and each ticked follow-up
 * becomes a task (Work queue, Calls lane). Nothing is texted or written to
 * the PMS. Filing twice adds nothing: the log entry and the tasks are keyed
 * to the call.
 */
export const SummaryFileRequestSchema = z
  .object({
    /** Follow-ups to turn into tasks, as reviewed (edited) by staff. */
    followUps: z.array(z.string().trim().min(1).max(200)).max(3).default([]),
    /** Put the summary on the patient's contact log (needs a matched patient). */
    logContact: z.boolean().default(true),
  })
  .strict();
export type SummaryFileRequest = z.input<typeof SummaryFileRequestSchema>;

export const CallSummaryFiledSchema = z.object({
  at: Iso,
  byName: z.string().nullable(),
  /** The summary is on the patient's contact log. */
  contactLogged: z.boolean(),
  /** Open or closed tasks made from this call's follow-ups. */
  taskCount: z.number().int().nonnegative(),
});
export type CallSummaryFiled = z.infer<typeof CallSummaryFiledSchema>;

export const CallWrapupSchema = z.object({
  outcome: WrapupOutcomeSchema,
  reason: NotBookedReasonSchema.nullable(),
  note: z.string().nullable(),
  source: OutcomeSourceSchema,
  byName: z.string().nullable(),
  at: Iso,
  appointmentId: Uuid.nullable(),
});
export type CallWrapup = z.infer<typeof CallWrapupSchema>;

export const CallDetailSchema = CallRowSchema.extend({
  provider: CallSourceProviderSchema,
  /** Dialled office number (E.164). */
  did: E164Schema.nullable(),
  legs: z.array(CallLegSchema),
  /** The call path as one human sentence ("Main line, rang 3 desks for 28 s, voicemail"). */
  flow: z.string(),
  officeName: z.string(),
  /**
   * The office whose desk answered, when it is not the call's office: a
   * roll-over between offices that share one 3CX (Zand: the caller chose
   * Lansing, a Saginaw desk answered). The call counts for the office the
   * caller chose; this says where it was picked up.
   */
  answeredAtOfficeName: z.string().nullable().default(null),
  recording: z.object({
    available: z.boolean(),
    durationSeconds: z.number().int().nonnegative().nullable(),
    /** Why there is no audio, in plain English, when unavailable. */
    reason: z.string().nullable(),
  }),
  transcript: z
    .object({
      engine: z.string(),
      segments: z.array(TranscriptSegmentSchema),
    })
    .nullable(),
  summary: CallSummarySchema.nullable(),
  summarizer: z.object({ available: z.boolean(), reason: z.string().nullable() }),
  /** Set once staff filed the summary (contact log and follow-up tasks). */
  summaryFiled: CallSummaryFiledSchema.nullable().default(null),
  wrapup: CallWrapupSchema.nullable(),
  /** Unscheduled treatment the caller still has (null without money.view or no patient). */
  atStakeCents: z.number().int().nonnegative().nullable(),
  /** callvault's analysis (feat/phones-ai-parity); text parts need patient.view. */
  ai: CallAiSchema.nullable().default(null),
  /** Set when the transcript is withheld (no patient.view at this office). */
  transcriptLocked: z.boolean().default(false),
  /** A voicemail left on this call can be played. */
  voicemailAvailable: z.boolean().default(false),
});
export type CallDetail = z.infer<typeof CallDetailSchema>;

export const WrapupRequestSchema = z
  .object({
    outcome: WrapupOutcomeSchema,
    reason: NotBookedReasonSchema.optional(),
    note: z.string().trim().max(2000).optional(),
    appointmentId: Uuid.optional(),
  })
  .refine((v) => v.outcome === 'not_booked' || v.reason === undefined, {
    message: 'A reason applies to "Not booked" only',
    path: ['reason'],
  });
export type WrapupRequest = z.infer<typeof WrapupRequestSchema>;

export const RecordingTokenResponseSchema = z.object({
  /**
   * Single-use, single-purpose, expires within 5 minutes. Redeem it once with
   * `POST /v1/phones/recordings/stream` (body, never a URL or query string).
   */
  token: z.string().min(20).max(1000),
  expiresAt: Iso,
});

export const RecordingStreamRequestSchema = z.object({ token: z.string().min(20).max(1000) });
export type RecordingTokenResponse = z.infer<typeof RecordingTokenResponseSchema>;

/* ------------------------------------------------------------------------ */
/* Caller context (the call pop body)                                        */
/* ------------------------------------------------------------------------ */

export const EligibilityChipSchema = z.object({
  state: z.enum(['verified', 'unable', 'ineligible', 'not_checked']),
  at: Iso.nullable(),
});

export const HouseholdMemberSchema = z.object({
  patientId: Uuid,
  name: z.string(),
  relation: z.string(),
  isGuarantor: z.boolean(),
  age: z.number().int().nonnegative().nullable(),
  nextAppointmentAt: Iso.nullable(),
  recallDue: IsoDate.nullable(),
  balanceCents: z.number().int().nullable(),
  /** False for a member with no phone of their own (call and text disabled). */
  hasPhone: z.boolean(),
  onThisNumber: z.boolean(),
});
export type HouseholdMember = z.infer<typeof HouseholdMemberSchema>;

export const PatientCardSchema = z.object({
  id: Uuid,
  firstName: z.string(),
  lastName: z.string(),
  preferredName: z.string().nullable(),
  age: z.number().int().nonnegative().nullable(),
  status: z.string(),
  chartNumber: z.string().nullable(),
  homeLocationId: Uuid.nullable(),
  homeOfficeName: z.string().nullable(),
  alerts: z.array(z.object({ kind: z.string(), description: z.string() })),
  premed: z.boolean(),
  nextAppointment: z
    .object({
      id: Uuid,
      at: Iso,
      typeName: z.string().nullable(),
      providerAbbr: z.string().nullable(),
      confirmation: z.string(),
      locationId: Uuid,
    })
    .nullable(),
  lastVisit: IsoDate.nullable(),
  money: z
    .object({
      balanceCents: z.number().int(),
      over90Cents: z.number().int().nonnegative(),
      /** Open claims' estimated insurance payments not yet received (U/H/W/S claims). */
      insurancePendingCents: z.number().int().nonnegative(),
    })
    .nullable(),
  /** Staff-only patient grade letter (patient.view, same as the card); null when ungraded. */
  grade: z
    .object({ letter: z.enum(['A', 'B', 'C', 'D', 'F']), provisional: z.boolean() })
    .nullable(),
  /** Open tasks about this patient (newest due first, up to three). */
  openTasks: z.array(z.object({ id: Uuid, title: z.string(), dueAt: Iso.nullable() })),
  unscheduled: z.object({
    count: z.number().int().nonnegative(),
    /** Up to three CDT codes with tooth, for the pop line. */
    items: z.array(z.string()),
    cents: z.number().int().nonnegative().nullable(),
  }),
  recallDue: IsoDate.nullable(),
  insurance: z.object({ carrier: z.string(), planType: z.string() }).nullable(),
  eligibility: EligibilityChipSchema,
  household: z.array(HouseholdMemberSchema),
  lastTexts: z.array(
    z.object({ at: Iso, direction: z.enum(['inbound', 'outbound']), body: z.string() }),
  ),
  lastCall: z
    .object({
      at: Iso,
      label: z.string(),
      /** Call analytics summary of that call (callvault mining or the AI summary), PHI. */
      summary: z.string().nullable(),
      /** callvault's AI review of that call (phones-ai parity), when it has been analysed. */
      reasonLabel: z.string().nullable(),
      outcomeLabel: z.string().nullable(),
      needsFollowUp: z.boolean().nullable(),
      /** callvault triaged that call or voicemail as urgent. */
      urgent: z.boolean(),
    })
    .nullable(),
  /** "Why they're probably calling", deterministic rules, most relevant first. */
  why: z.array(z.string()),
});
export type PatientCard = z.infer<typeof PatientCardSchema>;

export const CallerCandidateSchema = z.object({
  patientId: Uuid,
  name: z.string(),
  status: z.string(),
  isGuarantor: z.boolean(),
  nextAppointmentAt: Iso.nullable(),
});

export const CallerContextSchema = z.object({
  number: E164Schema.nullable(),
  kind: CallerKindSchema,
  /** For `own_number`: which office line it is ("Saginaw, main line"). */
  ownNumberLabel: z.string().nullable(),
  candidates: z.array(CallerCandidateSchema),
  selectedPatientId: Uuid.nullable(),
  shared: z.boolean(),
  leadName: z.string().nullable(),
  patient: PatientCardSchema.nullable(),
  /**
   * Recent calls with this number (newest first, up to five): the unknown
   * caller's history ("called twice last week, missed"). Empty for withheld
   * and own numbers.
   */
  recentCalls: z.array(z.object({ at: Iso, label: z.string(), officeName: z.string().nullable() })),
  /**
   * Caller ID name from a CNAM lookup (Twilio) for a number on no patient,
   * family or named lead. Caller ID only, never a verified person; null when
   * none is on file.
   */
  callerIdName: z.string().nullable().default(null),
});
export type CallerContext = z.infer<typeof CallerContextSchema>;

export const CallerLookupQuerySchema = z.object({
  number: z.string().trim().min(1).max(40),
  /** Show this household member instead of the default pick. */
  patientId: Uuid.optional(),
});

export const CallerChoiceRequestSchema = z.object({
  number: E164Schema,
  patientId: Uuid,
  /** The live call this choice belongs to: links the caller to it (booked auto-tick). */
  liveCallId: Uuid.optional(),
});

/* ------------------------------------------------------------------------ */
/* Live calls                                                                */
/* ------------------------------------------------------------------------ */

export const LiveStateSchema = z.enum(['ringing', 'active', 'hold', 'ended']);
export type LiveState = z.infer<typeof LiveStateSchema>;

export const LiveCallSchema = z.object({
  id: Uuid,
  locationId: Uuid,
  officeName: z.string(),
  state: LiveStateSchema,
  direction: CallDirectionSchema,
  callerNumber: E164Schema.nullable(),
  /**
   * Caller ID name (CNAM): from the carrier, or from a caller ID lookup when
   * the PBX sends none (3CX) and the number matches no patient. Never a
   * patient name; shown as caller ID.
   */
  callerName: z.string().nullable(),
  lineLabel: z.string().nullable(),
  ringingExtensions: z.array(z.string()),
  answeredByName: z.string().nullable(),
  startedAt: Iso,
  answeredAt: Iso.nullable(),
  endedAt: Iso.nullable(),
  /** Ringing (or answered at) one of the viewer's own extensions. */
  mine: z.boolean(),
  /** `answer` when the source can only pop after pickup (3CX). */
  triggeredBy: z.enum(['ring', 'answer']),
  provider: PhonesProviderSchema,
  /** The final call record, once the call has ended. */
  callId: Uuid.nullable(),
  patientId: Uuid.nullable(),
  /** An appointment was booked for the caller while the call was up (wrap-up auto-tick). */
  bookedAppointmentId: Uuid.nullable(),
  /** The finished call already has its wrap-up outcome. */
  wrappedUp: z.boolean(),
  /** Answered by the viewer (their wrap-up to do). */
  answeredByMe: z.boolean(),
  /**
   * Why this call pops for the viewer: their desk rings (`extension`), the
   * office pops every call (`office`), they opted in to every call at the
   * office (`optin`), or they answered it (`answered`). Null: no pop.
   */
  popReason: z.enum(['extension', 'office', 'optin', 'answered']).nullable(),
  /** Why texting the caller is off at this office; null when texting works. */
  textingOff: z.string().nullable(),
});
export type LiveCall = z.infer<typeof LiveCallSchema>;

export const LiveResponseSchema = z.object({
  calls: z.array(LiveCallSchema),
  me: z.object({
    extensions: z.array(z.object({ extension: z.string(), locationId: Uuid })),
    presence: z.enum(['available', 'busy', 'dnd']),
  }),
  serverTime: Iso,
});
export type LiveResponse = z.infer<typeof LiveResponseSchema>;

export const LiveActionSchema = z.enum(['answer', 'hold', 'resume', 'end', 'decline']);
export type LiveAction = z.infer<typeof LiveActionSchema>;

export const LiveActionRequestSchema = z.object({ action: LiveActionSchema });

/**
 * `POST /v1/phones/live/:id/patient`: the pop's card click or pick-list choice
 * links the call to this patient (the call log row and its timeline entry
 * carry the patient). Never guessed for a shared number: staff choose.
 */
export const LivePatientLinkRequestSchema = z.object({ patientId: Uuid });
export type LivePatientLinkRequest = z.infer<typeof LivePatientLinkRequestSchema>;

/** `POST /v1/phones/live/:id/note`: a call-log note on the caller's timeline. */
export const LiveNoteRequestSchema = z.object({ body: z.string().trim().min(1).max(2000) });
export type LiveNoteRequest = z.infer<typeof LiveNoteRequestSchema>;

export const SimulateScenarioSchema = z.enum(['patient', 'family', 'unknown', 'own_number']);
export type SimulateScenario = z.infer<typeof SimulateScenarioSchema>;

export const SimulateRequestSchema = z.object({
  scenario: SimulateScenarioSchema.default('patient'),
});

export const PresenceRequestSchema = z.object({
  presence: z.enum(['available', 'busy', 'dnd']),
});

/* ------------------------------------------------------------------------ */
/* Click-to-call                                                             */
/* ------------------------------------------------------------------------ */

export const DialRequestSchema = z.object({
  to: z.string().trim().min(2).max(40),
  patientId: Uuid.optional(),
  /** Desk to ring first; defaults to the caller's own mapped extension. */
  extension: z
    .string()
    .regex(/^\d{2,6}$/)
    .optional(),
});
export type DialRequest = z.infer<typeof DialRequestSchema>;

export const DialResultSchema = z.object({
  liveCallId: Uuid.nullable(),
  /** "Ringing your ext 105 first, then dialing (989) 555-0123". */
  message: z.string(),
});
export type DialResult = z.infer<typeof DialResultSchema>;

/* ------------------------------------------------------------------------ */
/* Unknown caller -> prospective patient                                     */
/* ------------------------------------------------------------------------ */

export const ProspectRequestSchema = z.object({
  firstName: z.string().trim().min(1).max(60),
  lastName: z.string().trim().min(1).max(60),
  birthdate: IsoDate,
  phone: E164Schema,
  liveCallId: Uuid.optional(),
  callId: Uuid.optional(),
});
export type ProspectRequest = z.infer<typeof ProspectRequestSchema>;

export const ProspectResultSchema = z.object({ patientId: Uuid });

/* ------------------------------------------------------------------------ */
/* Overview: KPIs, team, lines                                               */
/* ------------------------------------------------------------------------ */

/** A ratio kept as numerator and denominator so offices roll up as ratio of sums. */
export const RatioSchema = z.object({
  num: z.number().int().nonnegative(),
  den: z.number().int().nonnegative(),
});
export type Ratio = z.infer<typeof RatioSchema>;

/* ------------------------------------------------------------------------ */
/* Open hours vs after hours (answer and missed rates by window)             */
/* ------------------------------------------------------------------------ */

/**
 * When an inbound call came in, judged against the office's CURRENT configured
 * hours and holidays: open; lunch (closed between two ranges of a day);
 * before_after (before the first or after the last range of an open day);
 * closed_day (no hours that weekday, or a holiday); unknown (no hours set).
 */
export const HoursWindowSchema = z.enum(['open', 'lunch', 'before_after', 'closed_day', 'unknown']);
export type HoursWindow = z.infer<typeof HoursWindowSchema>;

/** Inbound calls of one window (spam, own numbers and test calls excluded). */
export const WindowStatsSchema = z.object({
  inbound: z.number().int().nonnegative(),
  /** A person or the AI receptionist talked to the caller. */
  answered: z.number().int().nonnegative(),
  /** Rang a desk or ring group and nobody picked up. */
  missed: z.number().int().nonnegative(),
  /** Hung up in the menu or while waiting in a queue. */
  abandoned: z.number().int().nonnegative(),
  voicemail: z.number().int().nonnegative(),
  answerRate: RatioSchema,
  /** Not answered (missed + abandoned + voicemail) / inbound. */
  missedRate: RatioSchema,
  /** Ring seconds summed over answered calls. */
  ringToAnswer: RatioSchema,
  /**
   * HOTFIX-MISSED-CALLS: what really happened to the calls not answered
   * (phones/miss-kind.ts). truly + recovered + short + redial = missed +
   * abandoned + voicemail.
   */
  /**
   * A missed caller nobody on staff reached within the hour: THE "missed"
   * number. Who is a missed caller is the missed-call engine's own rule
   * (telephony-gateway missed-caller.ts): a caller the engine texted is here
   * or in `recovered`, never in `shortAbandon`.
   */
  trulyMissed: z.number().int().nonnegative(),
  /**
   * Of trulyMissed: the engine texted them and nobody on staff has reached
   * them since ("missed: texted, waiting on us").
   */
  textedWaiting: z.number().int().nonnegative().default(0),
  /**
   * A person on staff reached the caller within an hour: answered their next
   * call, called them and connected, or texted them. The automatic text and
   * the caller's own text reply do not count.
   */
  recovered: z.number().int().nonnegative(),
  /** Pocket dials: under 10 s with no menu choice, not returning the office's call. */
  shortAbandon: z.number().int().nonnegative(),
  /**
   * The same caller rang again within 2 minutes: they got through (answered),
   * or the later call is the counted miss (one person counted once).
   */
  redialed: z.number().int().nonnegative(),
  /** trulyMissed / inbound: the headline "missed" rate. */
  trulyMissedRate: RatioSchema,
  /**
   * W5 (2026-10-28) missed-call callbacks, over the missed callers
   * (trulyMissed + recovered) who needed one: a caller who rang again and got
   * a person before anybody called them (`callerGotThrough`) needed none and
   * is counted apart (2026-11-02). callbackDue = calledBack + textedBack +
   * notCalledBack. Zero everywhere when the view does not load outbound calls.
   */
  callbacks: z
    .object({
      callbackDue: z.number().int().nonnegative(),
      /** The office dialled the caller within 24 h (any outcome). */
      calledBack: z.number().int().nonnegative(),
      calledBackWithinHour: z.number().int().nonnegative(),
      /** Minutes from the miss to the first callback, summed over calledBack (average = / calledBack). */
      callbackMinutes: z.number().int().nonnegative(),
      /** A person on staff texted them first (not the automatic text). */
      textedBack: z.number().int().nonnegative().default(0),
      /** The caller rang again and got a person before anyone called them (not in callbackDue). */
      callerGotThrough: z.number().int().nonnegative(),
      notCalledBack: z.number().int().nonnegative(),
      /** Of the truly missed (nobody reached them within the hour): called back later. */
      trulyCalledBack: z.number().int().nonnegative(),
    })
    .default({
      callbackDue: 0,
      calledBack: 0,
      calledBackWithinHour: 0,
      callbackMinutes: 0,
      textedBack: 0,
      callerGotThrough: 0,
      notCalledBack: 0,
      trulyCalledBack: 0,
    }),
});
export type WindowStats = z.infer<typeof WindowStatsSchema>;

export const HoursSplitSchema = z.object({
  /** During open hours: the staff-performance number. */
  open: WindowStatsSchema,
  /** Lunch, before/after hours and closed days. */
  afterHours: WindowStatsSchema,
  /** Whole business: every counted inbound call, any time. */
  all: WindowStatsSchema,
  afterHoursBy: z.object({
    lunch: z.number().int().nonnegative(),
    beforeAfter: z.number().int().nonnegative(),
    closedDay: z.number().int().nonnegative(),
  }),
  /** Calls at offices with no hours configured: in "all" only. */
  unknownHours: z.number().int().nonnegative(),
});
export type HoursSplit = z.infer<typeof HoursSplitSchema>;

export const OfficeHoursNoteSchema = z.object({
  locationId: Uuid,
  officeName: z.string(),
  hoursSet: z.boolean(),
  /** "Mon-Thu 8:30am-1pm, 2pm-5pm; Fri closed"; null when not set. */
  summary: z.string().nullable(),
  /** Some day has a mid-day break. */
  lunch: z.boolean(),
});
export type OfficeHoursNote = z.infer<typeof OfficeHoursNoteSchema>;

export const ANSWER_PERIODS = ['today', '7d', '30d'] as const;
export const AnswerPeriodSchema = z.enum(ANSWER_PERIODS);
export type AnswerPeriod = z.infer<typeof AnswerPeriodSchema>;

/** GET /v1/phones/answer-windows: today, 7 and 30 days, split by window, plus a daily trend. */
export const AnswerWindowsSchema = z.object({
  generatedAt: Iso,
  periods: z.array(
    z.object({
      key: AnswerPeriodSchema,
      label: z.string(),
      from: IsoDate,
      to: IsoDate,
      split: HoursSplitSchema,
    }),
  ),
  /** Office-local days, oldest first (30 days). */
  trend: z.array(
    z.object({
      date: IsoDate,
      open: RatioSchema,
      afterHours: RatioSchema,
      all: RatioSchema,
    }),
  ),
  offices: z.array(
    OfficeHoursNoteSchema.extend({
      periods: z.array(z.object({ key: AnswerPeriodSchema, split: HoursSplitSchema })),
    }),
  ),
});
export type AnswerWindows = z.infer<typeof AnswerWindowsSchema>;

export const PhoneKpisSchema = z.object({
  inbound: z.number().int().nonnegative(),
  inboundPrev: z.number().int().nonnegative(),
  answerRate: RatioSchema,
  missed: z.number().int().nonnegative(),
  voicemail: z.number().int().nonnegative(),
  /** Missed or voicemail callers later reached or booked the same day. */
  recovered: z.number().int().nonnegative(),
  booked: z.number().int().nonnegative(),
  bookedCents: z.number().int().nonnegative().nullable(),
  atRiskCallers: z.number().int().nonnegative(),
  atRiskCents: z.number().int().nonnegative().nullable(),
  /** Seconds, as a sum and a count (ratio of sums across offices). */
  ringToAnswer: RatioSchema,
  hourly: z.array(z.number().int().nonnegative()),
  /** Inbound callers who hung up while waiting in a PBX queue today. */
  abandoned: z.number().int().nonnegative(),
  /** Inbound calls today that came in while the office was closed (configured hours only). */
  afterHours: z.number().int().nonnegative(),
  /** Distinct numbers no patient holds that called in today (prospects, one per number). */
  newPatient: z.number().int().nonnegative(),
  /** Inbound patient callers in the last 7 days who got an appointment within 7 days of the call. */
  bookedWithin7d: RatioSchema,
  /**
   * Today's inbound split by window: open hours (the headline answer rate),
   * after hours and the whole business. `answerRate` above is the whole business.
   */
  split: HoursSplitSchema,
  /** Each office's configured hours (offices without hours say so on the card). */
  hours: z.array(OfficeHoursNoteSchema),
});
export type PhoneKpis = z.infer<typeof PhoneKpisSchema>;

export const TeamMemberSchema = z.object({
  extension: z.string(),
  locationId: Uuid,
  userId: Uuid.nullable(),
  name: z.string(),
  kind: z.enum(['desk', 'ring_group', 'ai', 'voicemail']),
  state: z.enum(['available', 'ringing', 'talk', 'hold', 'busy', 'dnd']),
  you: z.boolean(),
});
export type TeamMember = z.infer<typeof TeamMemberSchema>;

/**
 * `unavailable` = the office has no phone system connected, so nothing can be
 * claimed either way (audit PH-F-03: the mock's "verified" was shown for every
 * real office).
 */
export const ProviderCapabilitiesSchema = z.object({
  ringTimePop: z.enum(['realtime', 'poll', 'after-answer', 'none', 'unavailable']),
  clickToCall: z.enum(['verified', 'unverified', 'unsupported', 'unavailable']),
  recordings: z.enum(['verified', 'unverified', 'unsupported', 'unavailable']),
  transcripts: z.enum(['verified', 'unverified', 'unsupported', 'unavailable']),
  notes: z.array(z.string()),
});

/**
 * How an office's phone data arrives (audit PH-F-01/F-02):
 * - `not_connected`: no phone system configured; no calls, no simulator.
 * - `awaiting_data`: a PBX is configured but no call history has arrived yet.
 * - `connected`: call history is arriving from the PBX (CDR ingest or events).
 * - `simulated`: development only, or an explicit demo office with
 *   VELORA_ALLOW_PHONE_SIMULATOR=1; calls are synthetic.
 */
export const PhonesConnectionStateSchema = z.enum([
  'not_connected',
  'awaiting_data',
  'connected',
  'simulated',
]);
export type PhonesConnectionState = z.infer<typeof PhonesConnectionStateSchema>;

/** Office open now: `unknown` when no hours are set ("Hours not set", PH-F-05). */
export const OfficeOpenStateSchema = z.enum(['open', 'closed', 'unknown']);
export type OfficeOpenState = z.infer<typeof OfficeOpenStateSchema>;

/** Where call history comes from: a read-only pull, a pushing exporter, or nothing. */
export const CdrSourceKindSchema = z.enum(['none', 'pull', 'push']);
export type CdrSourceKind = z.infer<typeof CdrSourceKindSchema>;

export const CdrStatusSchema = z.object({
  source: CdrSourceKindSchema,
  /** The 3CX department (group) name whose calls belong to this office. */
  group: z.string().nullable(),
  /** Last 4 characters of the push ingest key, when one is issued. */
  ingestKeyHint: z.string().nullable(),
  lastSuccessAt: Iso.nullable(),
  lastError: z.string().nullable(),
  /** Calls ingested from the PBX in the last 30 days. */
  calls30d: z.number().int().nonnegative(),
});
export type CdrStatus = z.infer<typeof CdrStatusSchema>;

export const OfficeLineSchema = z.object({
  id: Uuid.nullable(),
  number: E164Schema,
  label: z.string(),
  kind: z.enum(['main', 'new_patients', 'billing', 'fax', 'tracking', 'other']),
  smsCapable: z.boolean(),
  campaign: z.string().nullable(),
});
export type OfficeLine = z.infer<typeof OfficeLineSchema>;

/**
 * The office's 3CX call-record feed (V02's pull job or push exporter).
 * `connected` only while the newest successful batch is under 15 minutes old:
 * the pull runs every 5 minutes and a quiet push window still posts, so
 * silence means a gap.
 */
export const CdrFeedSchema = z.object({
  state: z.enum(['connected', 'stale', 'not_connected']),
  lastBatchAt: Iso.nullable(),
});
export type CdrFeed = z.infer<typeof CdrFeedSchema>;

/**
 * Where this office's call numbers come from: real PBX call records (`pbx`:
 * a CDR feed, or real-provider calls from any ingest in the last 30 days),
 * the development simulator (`simulator`, never in production), or nothing
 * yet (`none`: KPIs say "not available" instead of showing zeros).
 */
export const CallDataSourceSchema = z.enum(['pbx', 'simulator', 'none']);
export type CallDataSource = z.infer<typeof CallDataSourceSchema>;

export const OfficePhoneSchema = z.object({
  locationId: Uuid,
  officeName: z.string(),
  /** The PBX this office is on; null when no phone system is configured. */
  provider: PhonesProviderSchema.nullable(),
  /**
   * `configured` in phones settings, `inferred` from real PBX call history,
   * `none` when nothing is configured (shown as "Not configured", PH-F-10).
   */
  providerSource: z.enum(['configured', 'inferred', 'none']),
  state: PhonesConnectionStateSchema,
  /** Events and calls come from the simulator, not the PBX. */
  simulated: z.boolean(),
  openState: OfficeOpenStateSchema,
  cdr: CdrStatusSchema,
  /** True only when `hoursState` is `open`. */
  open: z.boolean(),
  /**
   * The one shared hours rule (`hoursState`, @velora/telephony-gateway), the
   * same answer the AI receptionist uses. `not_set`: no office hours entered.
   */
  hoursState: z.enum(['open', 'closed', 'not_set']),
  popAudience: z.enum(['extension', 'office']),
  capabilities: ProviderCapabilitiesSchema,
  lines: z.array(OfficeLineSchema),
  /**
   * T7f feed freshness, derived from V02's ingest health (`cdr.lastSuccessAt`):
   * named `cdrFeed` because V02's `cdr` (source, group, key hint) came first.
   */
  cdrFeed: CdrFeedSchema,
  callData: CallDataSourceSchema,
});
export type OfficePhone = z.infer<typeof OfficePhoneSchema>;

/**
 * `GET /v1/phones/status`: the cheap check the shell's global call pop host
 * makes on every page (no KPIs). The pop renders nothing when not connected.
 */
export const PhonesStatusSchema = z.object({
  connected: z.boolean(),
  simulatorAllowed: z.boolean(),
  offices: z.array(z.object({ locationId: Uuid, state: PhonesConnectionStateSchema })),
});
export type PhonesStatus = z.infer<typeof PhonesStatusSchema>;

/** `GET /v1/phones/patients/:id/number`: the number "Call patient" should dial (PT-18). */
export const PatientNumberSchema = z.object({
  patientId: Uuid,
  /** Primary phone first, then mobile; null when the patient has no phone on file. */
  number: E164Schema.nullable(),
  phoneType: z.enum(['mobile', 'home', 'work', 'other']).nullable(),
  homeLocationId: Uuid.nullable(),
});
export type PatientNumber = z.infer<typeof PatientNumberSchema>;

/**
 * Call -> booking -> production for the offices whose call data is real, from
 * the call-outcome correlation (first touch, bookings within 7 days, per
 * office). Money is null without `money.view`.
 */
export const CallAttributionSchema = z.object({
  days: z.number().int().positive(),
  inbound: z.number().int().nonnegative(),
  /** Missed or voicemail inbound calls. */
  missed: z.number().int().nonnegative(),
  calledBack: z.number().int().nonnegative(),
  recovered: z.number().int().nonnegative(),
  booked: z.number().int().nonnegative(),
  showed: z.number().int().nonnegative(),
  produced: z.number().int().nonnegative(),
  scheduledCents: z.number().int().nonnegative().nullable(),
  producedCents: z.number().int().nonnegative().nullable(),
  /** Missed callers (one per caller per office) with nothing booked yet. */
  stillUnscheduled: z.number().int().nonnegative(),
  correlatedAt: Iso.nullable(),
});
export type CallAttribution = z.infer<typeof CallAttributionSchema>;

export const PhonesOverviewSchema = z.object({
  date: IsoDate,
  /** Some office in scope has a phone system (or the simulator) connected. */
  connected: z.boolean(),
  /** The simulator may ring calls here (development, or an explicit demo office). */
  simulatorAllowed: z.boolean(),
  kpis: PhoneKpisSchema,
  /**
   * False when no office in scope has real call data (production before the
   * CDR feed is connected): the UI shows "Not available", never zeros.
   * KPIs and attribution cover only the offices whose `callData` is not `none`.
   */
  kpisAvailable: z.boolean(),
  /** Which offices are left out and why, in plain English; null when none are. */
  kpisNote: z.string().nullable(),
  /** Last 30 days; null when `kpisAvailable` is false. */
  attribution: CallAttributionSchema.nullable(),
  team: z.array(TeamMemberSchema),
  offices: z.array(OfficePhoneSchema),
});
export type PhonesOverview = z.infer<typeof PhonesOverviewSchema>;

/* ------------------------------------------------------------------------ */
/* Missed-call worklist                                                      */
/* ------------------------------------------------------------------------ */

/**
 * Text-back state, honestly: `sent` only when a carrier accepted it; `held`
 * while a hold (grace period, quiet hours, recording check) runs; `not_sent`
 * with the reason (texting off, carrier approval pending, opted out, landline,
 * live sends off...). `label` is plain English for the row.
 */
export const MissedTextStateSchema = z.object({
  state: z.enum(['sent', 'queued', 'held', 'not_sent']),
  label: z.string(),
  at: Iso.nullable(),
});
export type MissedTextState = z.infer<typeof MissedTextStateSchema>;

export const MissedFollowUpSchema = z.object({
  taskId: Uuid.nullable(),
  taskStatus: z.enum(['open', 'in_progress', 'done', 'cancelled']).nullable(),
  /** Outbound call attempts to the caller since the first miss. */
  callbackAttempts: z.number().int().nonnegative(),
  /** Why the callback closed, in plain English; null while open. */
  closedLabel: z.string().nullable(),
  text: MissedTextStateSchema.nullable(),
});
export type MissedFollowUp = z.infer<typeof MissedFollowUpSchema>;

export const MissedCallerSchema = z.object({
  /** E.164 of the caller (the worklist key). */
  number: E164Schema,
  locationId: Uuid,
  caller: CallCallerSchema,
  attempts: z.array(
    z.object({
      callId: Uuid,
      at: Iso,
      lineLabel: z.string().nullable(),
      disposition: CallDispositionSchema,
    }),
  ),
  lastAt: Iso,
  /** Unscheduled treatment for a patient, or the office's new-patient value. */
  atStakeCents: z.number().int().nonnegative().nullable(),
  atStakeLabel: z.string().nullable(),
  /** An automated text already went out after the miss. */
  textedAt: Iso.nullable(),
  /**
   * The caller's line type as the carrier reported it (cached Twilio Lookup or
   * a 30006 refusal); `landline`: cannot be texted. Absent from older servers.
   */
  lineType: z.enum(['mobile', 'landline', 'voip', 'unknown']).nullable().optional(),
  /** Reached again (a later connected call either way) or booked since. */
  resolved: z.object({ at: Iso, how: z.enum(['called_back', 'called_in', 'booked']) }).nullable(),
  /**
   * The missed-call engine's follow-up for this caller (newest evaluated
   * call): the callback task and what happened to the text-back. Null until
   * the call has been evaluated.
   */
  followUp: MissedFollowUpSchema.nullable(),
  /**
   * Called back after the last miss (Hayden R14, R16): the first outbound
   * call to this number from ANY office extension (call records), or a
   * manual "Called back" mark (a cell phone, a call outside the phone
   * system). `connected`: the callback was answered. Absent from older servers.
   */
  calledBack: z
    .object({
      at: Iso,
      source: z.enum(['call_record', 'manual']),
      connected: z.boolean().nullable(),
      /** The line or office the call back went out on, or who marked it. */
      by: z.string().nullable(),
      note: z.string().nullable(),
    })
    .nullable()
    .optional(),
});
export type MissedCaller = z.infer<typeof MissedCallerSchema>;

/** `GET /phones/missed?days=` (default 3, Hayden P9: was fixed at 3 days). */
export const MISSED_DAYS = [1, 3, 7, 14, 30] as const;
export const MissedQuerySchema = z.object({
  days: z.coerce
    .number()
    .int()
    .refine((d) => (MISSED_DAYS as readonly number[]).includes(d), {
      message: 'days must be 1, 3, 7, 14 or 30',
    })
    .default(3),
});
export type MissedQuery = z.infer<typeof MissedQuerySchema>;

export const MarkCalledBackSchema = z.object({
  /** The newest missed attempt of the caller. */
  callId: Uuid,
  note: z.string().trim().max(200).optional(),
});
export type MarkCalledBack = z.infer<typeof MarkCalledBackSchema>;

export const MissedListResponseSchema = z.object({
  /** The window the list covers, in days. */
  days: z.number().int().positive().optional(),
  items: z.array(MissedCallerSchema),
  /** Text-back automation as configured (owned by the missed-call module). */
  textBack: z.object({ enabled: z.boolean(), owner: z.string().nullable() }),
});
export type MissedListResponse = z.infer<typeof MissedListResponseSchema>;

/* ------------------------------------------------------------------------ */
/* Insights                                                                  */
/* ------------------------------------------------------------------------ */

export const InsightsQuerySchema = z.object({
  days: z.coerce
    .number()
    .int()
    .refine((d) => d === 7 || d === 30 || d === 90, {
      message: 'days must be 7, 30 or 90',
    }),
});

export const PhoneInsightsSchema = z.object({
  days: z.number().int(),
  /**
   * Offices in the selector left out because they do not have Call
   * Intelligence (owner role test: All offices showed one office's 37
   * inbound calls next to Call analytics' 191, with nothing saying why).
   */
  notIncluded: z.array(z.object({ locationId: z.uuid(), name: z.string() })).default([]),
  inbound: z.number().int().nonnegative(),
  answerRate: RatioSchema,
  /** Missed or voicemail callers who booked or were reached within 7 days. */
  recovered: RatioSchema,
  booked: z.number().int().nonnegative(),
  bookedCents: z.number().int().nonnegative().nullable(),
  daily: z.array(
    z.object({
      date: IsoDate,
      booked: z.number().int().nonnegative(),
      notBooked: z.number().int().nonnegative(),
      lost: z.number().int().nonnegative(),
    }),
  ),
  /** Missed inbound by weekday (0 = Sunday) and hour, office local time. */
  heat: z.array(
    z.object({
      weekday: z.number().int().min(0).max(6),
      hour: z.number().int().min(0).max(23),
      missed: z.number().int().nonnegative(),
    }),
  ),
  topics: z.array(z.object({ topic: z.string(), count: z.number().int().nonnegative() })),
  offices: z.array(
    z.object({
      locationId: Uuid,
      officeName: z.string(),
      inbound: z.number().int().nonnegative(),
      /** Whole business (every inbound call). */
      answerRate: RatioSchema,
      /** During the office's open hours only (staff performance). */
      openAnswerRate: RatioSchema,
      missed: z.number().int().nonnegative(),
    }),
  ),
  /** The range's inbound calls by window: open hours, after hours, whole business. */
  split: HoursSplitSchema,
});
export type PhoneInsights = z.infer<typeof PhoneInsightsSchema>;

/* ------------------------------------------------------------------------ */
/* Analytics (date range, per office, Dental Intel-style)                    */
/* ------------------------------------------------------------------------ */

/** Longest range the analytics endpoint accepts, in days (inclusive). */
export const PHONE_ANALYTICS_MAX_DAYS = 366;

export const PhoneAnalyticsQuerySchema = z
  .object({
    /** First office-local day, inclusive. */
    from: IsoDate,
    /** Last office-local day, inclusive. */
    to: IsoDate,
  })
  .refine((q) => q.from <= q.to, { message: 'from must be on or before to', path: ['from'] })
  .refine(
    (q) =>
      (Date.parse(`${q.to}T00:00:00Z`) - Date.parse(`${q.from}T00:00:00Z`)) / 86_400_000 <
      PHONE_ANALYTICS_MAX_DAYS,
    { message: `The range can be at most ${PHONE_ANALYTICS_MAX_DAYS} days`, path: ['to'] },
  );
export type PhoneAnalyticsQuery = z.infer<typeof PhoneAnalyticsQuerySchema>;

const Count = z.number().int().nonnegative();

/** Steps of an inbound call's path (PhoneAnalytics.paths). */
export const PHONE_PATH_STEPS = [
  'office_line',
  'ai_line',
  'staff',
  'ai',
  'missed',
  'abandoned',
  'voicemail',
  'relayed',
  'transferred',
  'booked',
  'called_back',
  'recovered',
  'no_callback',
] as const;
export type PhonePathStep = (typeof PHONE_PATH_STEPS)[number];

export const PhoneAnalyticsTotalsSchema = z.object({
  /** Inbound calls, spam and the office's own numbers excluded. */
  inbound: Count,
  outbound: Count,
  /** A person (or the AI receptionist) talked to the caller. */
  answered: Count,
  /** Rang a desk or ring group and nobody picked up. */
  missed: Count,
  /** Hung up before reaching a person: in the IVR/menu or waiting in a queue. */
  abandoned: Count,
  voicemail: Count,
  /** Missed while the office was closed (configured hours only). */
  afterHoursMissed: Count,
  /** Inbound calls that came in while the office was closed, any outcome. */
  afterHours: Count,
  spam: Count,
  answerRate: RatioSchema,
  /** Seconds from first ring to answer, summed over answered calls. */
  ringToAnswer: RatioSchema,
  outboundConnected: Count,
  /** Talk time on answered inbound and connected outbound calls. */
  talkSeconds: Count,
  /** Distinct callers who matched no existing patient (by phone) when they called. */
  newPatientCallers: Count,
  /** Of those, callers who became patients (first visit on or after the call) or booked. */
  newPatientConverted: Count,
  existingPatientCallers: Count,
  /** Calls with an appointment booked during or within an hour of the call. */
  bookedOnCall: Count,
  /** Patient callers (distinct) who had an appointment booked within 7 days. */
  bookedWithin7d: RatioSchema,
  /** Production scheduled on appointments booked on a call (null without money.view). */
  bookedCents: z.number().int().nonnegative().nullable(),
  /** Distinct callers with a missed, abandoned or voicemail call in range. */
  missedCallers: Count,
  /** Of those: an outbound call to them was attempted afterwards. */
  calledBack: Count,
  /** Of those: no callback attempt, never got through and never booked (as of now). */
  noCallback: Count,
  /** Median minutes from the miss to the first callback attempt. */
  callbackMedianMinutes: z.number().nonnegative().nullable(),
  /** Inbound calls the AI receptionist answered (its own number or a forward), counted once. */
  aiAnswered: Count,
  /** Of those, calls that first rang the office line in 3CX and were forwarded to the AI. */
  aiForwarded: Count,
  aiAfterHours: Count,
  /** The AI relayed the caller's message to the office or the on-call doctor. */
  aiRelayed: Count,
  /** The AI's triage was urgent or emergency. */
  aiUrgent: Count,
  /** Booking requests the AI filed during the call. */
  aiBookingRequests: Count,
  /** AI callers matched to a patient with an appointment booked within 7 days. */
  aiBooked: Count,
  /** AI calls that ended in a live transfer to staff (recep.client does not transfer today). */
  aiTransferred: Count,
  /** AI callers a staff member called back afterwards. */
  aiCalledBack: Count,
  /** After-hours inbound calls answered by anyone (staff or AI) / all after-hours inbound. */
  afterHoursCoverage: RatioSchema,
});
export type PhoneAnalyticsTotals = z.infer<typeof PhoneAnalyticsTotalsSchema>;

export const PhoneAnalyticsStaffSchema = z.object({
  locationId: Uuid,
  extension: z.string(),
  name: z.string().nullable(),
  userId: Uuid.nullable(),
  /** Inbound calls this extension answered. */
  answered: Count,
  /** Inbound calls that rang this extension and nobody answered anywhere. */
  missedRinging: Count,
  outbound: Count,
  outboundConnected: Count,
  talkSeconds: Count,
  /** Ring seconds summed over the inbound calls it answered. */
  ringToAnswer: RatioSchema,
});
export type PhoneAnalyticsStaff = z.infer<typeof PhoneAnalyticsStaffSchema>;

export const PhoneAnalyticsSchema = z.object({
  from: IsoDate,
  to: IsoDate,
  generatedAt: Iso,
  totals: PhoneAnalyticsTotalsSchema,
  offices: z.array(
    z.object({
      locationId: Uuid,
      officeName: z.string(),
      /** Where this office's calls come from: the PBX, the dev simulator, or nothing yet. */
      callData: CallDataSourceSchema,
      provider: PhonesProviderSchema.nullable(),
      lastCallAt: Iso.nullable(),
      totals: PhoneAnalyticsTotalsSchema,
    }),
  ),
  /** Inbound by office-local hour (0-23). */
  byHour: z.array(
    z.object({
      hour: z.number().int().min(0).max(23),
      inbound: Count,
      answered: Count,
      missed: Count,
    }),
  ),
  /** Inbound by office-local weekday (0 = Sunday). */
  byWeekday: z.array(
    z.object({
      weekday: z.number().int().min(0).max(6),
      inbound: Count,
      answered: Count,
      missed: Count,
    }),
  ),
  /** Inbound weekday by hour; `missed` counts missed, abandoned and voicemail. */
  heat: z.array(
    z.object({
      weekday: z.number().int().min(0).max(6),
      hour: z.number().int().min(0).max(23),
      inbound: Count,
      missed: Count,
    }),
  ),
  daily: z.array(
    z.object({
      date: IsoDate,
      inbound: Count,
      answered: Count,
      missed: Count,
      outbound: Count,
    }),
  ),
  staff: z.array(PhoneAnalyticsStaffSchema),
  /**
   * What happened to inbound calls, step by step, counted once per call:
   * e.g. ["office_line", "ai", "relayed", "called_back"] or
   * ["office_line", "missed", "no_callback"].
   */
  paths: z.array(z.object({ path: z.array(z.enum(PHONE_PATH_STEPS)), calls: Count })),
});
export type PhoneAnalytics = z.infer<typeof PhoneAnalyticsSchema>;

/* ------------------------------------------------------------------------ */
/* Settings: extension mapping and lines                                     */
/* ------------------------------------------------------------------------ */

export const ExtensionKindSchema = z.enum(['desk', 'ring_group', 'ai', 'voicemail']);

export const ExtensionSchema = z.object({
  id: Uuid,
  locationId: Uuid,
  extension: z.string(),
  label: z.string(),
  kind: ExtensionKindSchema,
  userId: Uuid.nullable(),
  userName: z.string().nullable(),
});
export type Extension = z.infer<typeof ExtensionSchema>;

export const ExtensionUpsertSchema = z.object({
  extension: z.string().regex(/^\d{2,6}$/, 'Extensions are 2 to 6 digits'),
  label: z.string().trim().min(1).max(60),
  kind: ExtensionKindSchema.default('desk'),
  userId: Uuid.nullable().default(null),
});
export type ExtensionUpsert = z.infer<typeof ExtensionUpsertSchema>;

/**
 * `PUT /v1/phones/settings/pop-optin`: show this person every ringing call at
 * the office. Anyone may set their own; settings.manage sets it for others.
 */
export const PopOptInRequestSchema = z.object({
  userId: Uuid,
  on: z.boolean(),
});
export type PopOptInRequest = z.infer<typeof PopOptInRequestSchema>;

export const LineUpsertSchema = z.object({
  number: z.string().trim().min(7).max(40),
  label: z.string().trim().min(1).max(60),
  kind: OfficeLineSchema.shape.kind,
  smsCapable: z.boolean().default(false),
  campaign: z.string().trim().max(60).nullable().default(null),
});
export type LineUpsert = z.infer<typeof LineUpsertSchema>;

export const OfficeSettingsPatchSchema = z.object({
  provider: PhonesProviderSchema.optional(),
  popAudience: z.enum(['extension', 'office']).optional(),
  /** 3CX department (group) name, exactly as in 3CX ("A2 Dental"). */
  threecxGroup: z.string().trim().min(1).max(100).nullable().optional(),
  cdrSource: CdrSourceKindSchema.optional(),
});
export type OfficeSettingsPatch = z.infer<typeof OfficeSettingsPatchSchema>;

/** A push ingest key, shown ONCE when issued; only its hash is stored. */
export const IngestKeyResponseSchema = z.object({ key: z.string().min(40), hint: z.string() });
export type IngestKeyResponse = z.infer<typeof IngestKeyResponseSchema>;

/* ------------------------------------------------------------------------ */
/* CDR push ingest (read-only exporter -> Velora)                            */
/* ------------------------------------------------------------------------ */

/**
 * `POST /v1/phones/ingest/cdr`, `Authorization: Bearer <office ingest key>`.
 * Raw 3CX `cdroutput` legs (every leg of each call) and optional
 * `cdrrecordings` rows. The office comes from the key, never the body. Rows
 * are validated by `@velora/telephony-gateway` `CdrOutputRowSchema`.
 */
export const CDR_INGEST_MAX_ROWS = 5000;
export const CdrIngestRequestSchema = z.object({
  format: z.literal('threecx-cdroutput'),
  legs: z.array(z.record(z.string(), z.unknown())).max(CDR_INGEST_MAX_ROWS),
  recordings: z.array(z.record(z.string(), z.unknown())).max(CDR_INGEST_MAX_ROWS).default([]),
});
export type CdrIngestRequest = z.infer<typeof CdrIngestRequestSchema>;

export const CdrIngestResultSchema = z.object({
  received: z.number().int().nonnegative(),
  calls: z.number().int().nonnegative(),
  created: z.number().int().nonnegative(),
  updated: z.number().int().nonnegative(),
  /** System calls (voicemail console, park, feature codes) are not calls. */
  skipped: z.number().int().nonnegative(),
});
export type CdrIngestResult = z.infer<typeof CdrIngestResultSchema>;

/**
 * Caller ID lookups (CNAM) for callers that match no patient, per office
 * (Settings > Phones). One Twilio lookup per number per 30 days on the
 * office's Twilio account, billed as usage.
 */
export const CallerIdSettingsSchema = z.object({
  enabled: z.boolean(),
  /** Most paid lookups per calendar month at this office. */
  monthlyCap: z.number().int().min(0).max(20000),
  usedThisMonth: z.number().int().nonnegative(),
  /** The office has a Twilio account to look up with (its texting account). */
  accountReady: z.boolean(),
});
export type CallerIdSettings = z.infer<typeof CallerIdSettingsSchema>;

export const CallerIdSettingsPatchSchema = z
  .object({
    enabled: z.boolean().optional(),
    monthlyCap: z.number().int().min(0).max(20000).optional(),
  })
  .strict();
export type CallerIdSettingsPatch = z.infer<typeof CallerIdSettingsPatchSchema>;

/** `POST /v1/phones/settings/caller-id/backfill`: today's unmatched callers at the office. */
export const CallerIdBackfillResultSchema = z.object({
  /** Unmatched numbers that called today with no caller ID yet (at most 25). */
  numbers: z.number().int().nonnegative(),
  /** Paid lookups made. */
  lookedUp: z.number().int().nonnegative(),
  /** Numbers that now have a caller ID name. */
  found: z.number().int().nonnegative(),
  /** Not looked up: switched off, monthly cap reached, no Twilio account, or a failure. */
  skipped: z.number().int().nonnegative(),
  /** Today's call rows that now carry the name. */
  callsUpdated: z.number().int().nonnegative(),
});
export type CallerIdBackfillResult = z.infer<typeof CallerIdBackfillResultSchema>;

export const PhonesSettingsSchema = z.object({
  offices: z.array(
    OfficePhoneSchema.extend({
      extensions: z.array(ExtensionSchema),
      /** People who see every ringing call at this office (opt-in). */
      popOptIn: z.array(Uuid),
      callerId: CallerIdSettingsSchema.default({
        enabled: true,
        monthlyCap: 300,
        usedThisMonth: 0,
        accountReady: false,
      }),
    }),
  ),
  users: z.array(z.object({ id: Uuid, name: z.string(), locationIds: z.array(Uuid) })),
});
export type PhonesSettings = z.infer<typeof PhonesSettingsSchema>;

/** SSE entity key for live calls; ids only on the wire. */
export const PHONES_LIVE_ENTITY = 'phones.live';
/** SSE entity key for finished calls (the log, missed list and KPIs). */
export const PHONES_CALL_ENTITY = 'phones.call';
